import React, { useEffect } from "react";
import { RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { useAuth } from "../../lib/auth-context";
import { applyUpdate, useUpdatePostponed } from "../../lib/pwa";

import { useTranslation } from "react-i18next";

/** Strip height: one row on phones, so the app header always clears it. */
const BANNER_HEIGHT = "2.5rem";

/**
 * Left behind when the person answers "Later" to the update card: the tab keeps running
 * the old build, so the way to reload stays in the corner of the eye instead of vanishing.
 *
 * Shown only when no other strip is up (impersonation, break-glass club access): those
 * carry actions nobody can afford to have buried, and one row per screen is all there is.
 */
export function UpdateBanner() {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const postponed = useUpdatePostponed();
  const busyElsewhere = !!user?.impersonator || !!user?.accessGrant;
  const showing = postponed && !busyElsewhere;

  // The app header and sticky page bars sit below the strip: they read its height from --banner-h.
  useEffect(() => {
    if (!showing) return;
    const root = document.documentElement;
    root.style.setProperty("--banner-h", BANNER_HEIGHT);
    return () => {
      root.style.removeProperty("--banner-h");
    };
  }, [showing]);

  if (!showing) return null;

  return (
    <div
      className="sticky top-0 z-40 flex items-center gap-2 bg-primary px-3 text-sm font-medium text-primary-foreground sm:justify-center sm:gap-3 sm:px-4"
      style={{ height: BANNER_HEIGHT }}
      role="status"
      aria-live="polite"
    >
      <RefreshCw className="h-4 w-4 shrink-0" aria-hidden />
      <span className="min-w-0 truncate">{t("update.short")}</span>
      <Button size="sm" variant="secondary" onClick={applyUpdate} className="ml-auto h-7 shrink-0 px-2.5 text-xs sm:ml-0">
        {t("update.now")}
      </Button>
    </div>
  );
}
